import { useState } from 'react';
import logger from '@widgets/logger';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { ColorPicker } from '@components/ColorPicker';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import { Typography } from '@components/Typography';
import { Select } from '@components/Select';
import { useTranslation } from '@hooks';
import { KB } from '@constants/file';
import { BackgroundType, FontType, ButtonType } from '@constants/brands';
import { brandsService } from '@services';

const BlockWrapper = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: '16px',
    marginTop: '4px 0 16px',
});

const Appearance = () => {
    const [isUploading, setIsUploading] = useState(false);

    const { control, setValue, getValues, watch } = useFormContext();
    const { isSubmitting } = useFormState({ control });
    const { t } = useTranslation();

    const logo = watch('logo');

    const bgOptions = [
        { label: t('SettingsBrands.gradient'), value: BackgroundType.Gradient },
        { label: t('SettingsBrands.solid'), value: BackgroundType.Solid },
    ];

    const fontOptions = [
        { label: 'Arial', value: FontType.Arial, sx: { fontFamily: 'Arial' } },
        { label: 'Book Antiqua', value: FontType['Book Antiqua'], sx: { fontFamily: 'Book Antiqua' } },
        { label: 'Courier New', value: FontType['Courier New'], sx: { fontFamily: 'Courier New' } },
        { label: 'Georgia', value: FontType.Georgia, sx: { fontFamily: 'Georgia' } },
        { label: 'Helvetica', value: FontType.Helvetica, sx: { fontFamily: 'Helvetica' } },
        { label: 'Rubik', value: FontType.Rubik, sx: { fontFamily: 'Rubik' } },
        { label: 'Tahoma', value: FontType.Tahoma, sx: { fontFamily: 'Tahoma' } },
        { label: 'Times New Roman', value: FontType['Times New Roman'], sx: { fontFamily: 'Times New Roman' } },
        { label: 'Trebuchet MS', value: FontType['Trebuchet MS'], sx: { fontFamily: 'Trebuchet MS' } },
        { label: 'Verdana', value: FontType.Verdana, sx: { fontFamily: 'Verdana' } },
    ];

    const buttonOptions = [
        { label: t('SettingsBrands.sharp'), value: ButtonType.Sharp },
        { label: t('SettingsBrands.softlyRounded'), value: ButtonType['Softly Rounded'] },
        { label: t('SettingsBrands.rounded'), value: ButtonType.Rounded },
    ];

    const uploadLogo = async e => {
        try {
            setIsUploading(true);
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            const response = await brandsService.uploadLogo(data);
            setValue('logo', response.publicUrl, { shouldDirty: true });
        } catch (_) {
            logger.error("Couldn't upload logo for brand");
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const onDeleteLogo = () => setValue('logo', null, { shouldDirty: true });

    return (
        <>
            <Typography variant="subtitle1">{t('common.background')}</Typography>
            <BlockWrapper>
                <Controller
                    control={control}
                    name="backgroundStyle"
                    render={({ field, ref }) => (
                        <Select
                            {...field}
                            value={getValues('backgroundStyle')}
                            ref={ref}
                            label={t('common.style')}
                            options={bgOptions}
                            disabled={isSubmitting}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="backgroundColor"
                    render={({ field, fieldState: { error }, ref }) => (
                        <ColorPicker
                            ref={ref}
                            inputProps={{
                                ...field,
                                onChange: e => {
                                    const val = e?.target?.value ? e.target.value : e;
                                    field.onChange(val);
                                },
                            }}
                            label={t('common.color', { count: 1 })}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            disabled={isSubmitting}
                        />
                    )}
                />
            </BlockWrapper>
            <Typography variant="subtitle1">{t('common.font')}</Typography>
            <BlockWrapper>
                <Controller
                    control={control}
                    name="font"
                    render={({ field, ref }) => (
                        <Select
                            {...field}
                            value={getValues('font')}
                            ref={ref}
                            label={t('common.font')}
                            options={fontOptions}
                            disabled={isSubmitting}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="fontColor"
                    render={({ field, fieldState: { error }, ref }) => (
                        <ColorPicker
                            ref={ref}
                            inputProps={{
                                ...field,
                                onChange: e => {
                                    const val = e?.target?.value ? e.target.value : e;
                                    field.onChange(val);
                                },
                            }}
                            label={t('common.color', { count: 1 })}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            disabled={isSubmitting}
                        />
                    )}
                />
            </BlockWrapper>
            <Typography variant="subtitle1">{t('common.button', { count: 2 })}</Typography>
            <BlockWrapper>
                <Controller
                    control={control}
                    name="buttonStyle"
                    render={({ field, ref }) => (
                        <Select
                            {...field}
                            value={getValues('buttonStyle')}
                            ref={ref}
                            label={t('common.style')}
                            options={buttonOptions}
                            disabled={isSubmitting}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="buttonBackgroundColor"
                    render={({ field, fieldState: { error }, ref }) => (
                        <ColorPicker
                            ref={ref}
                            inputProps={{
                                ...field,
                                onChange: e => {
                                    const val = e?.target?.value ? e.target.value : e;
                                    field.onChange(val);
                                },
                            }}
                            label={t('common.color', { count: 1 })}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            disabled={isSubmitting}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="buttonFontColor"
                    render={({ field, fieldState: { error }, ref }) => (
                        <ColorPicker
                            ref={ref}
                            inputProps={{
                                ...field,
                                onChange: e => {
                                    const val = e?.target?.value ? e.target.value : e;
                                    field.onChange(val);
                                },
                            }}
                            label={t('SettingsBrands.fontColor')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            disabled={isSubmitting}
                        />
                    )}
                />
            </BlockWrapper>
            <Typography variant="subtitle1">{t('common.logo')}</Typography>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', margin: '4px 0 8px' }}>
                {t('common.imageUploadFormat', { size: '500KB' })}
            </Typography>
            <ImageUploaderSingle
                maxSize={KB * 500}
                minSize={10 * KB}
                extensions="image/*"
                cropConfig={{ aspectRatio: 1, outputWidth: 250 }}
                logoUrl={logo}
                disabled={isUploading || isSubmitting}
                onDelete={onDeleteLogo}
                uploadLogo={uploadLogo}
                style={{ marginBottom: 24 }}
                isUploading={isUploading}
            />
        </>
    );
};

export default Appearance;
